/* eslint-disable @typescript-eslint/camelcase */
import { setCookie, deleteCookie, ApiService } from '@zola-helpers/client';

import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';

import type { SaveResponseRequest, WClientUserAccountView } from '@zola/svc-web-api-ts-client';
import { BusinessUnitComponent } from 'components/onboard/types';
import * as userSelectors from '../selectors/user/userSelectors';

import * as ActionType from './types/UserActionTypes';
import type { UserActionTypes, AuthStatusType, UserContext } from './types/UserActionTypes';
import { detectMobileDevice } from '../util/detectBrowser';
import { trackIdentifyUser } from '../util/trackingHelper';

export type { UserContext };

export function requestUser(): UserActionTypes {
  return {
    type: ActionType.REQUEST_USER,
  };
}

export function receiveUser(response: UserContext): UserActionTypes {
  return {
    type: ActionType.RECEIVE_USER,
    payload: response,
  };
}

export function requestUserStatus(): UserActionTypes {
  return {
    type: ActionType.REQUEST_USER_STATUS,
  };
}

export function receiveUserStatus(response: { status: AuthStatusType }): UserActionTypes {
  return {
    type: ActionType.RECEIVE_USER_STATUS,
    payload: response,
  };
}

export function identifyUser(
  forceIdentify = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ThunkAction<void, any, unknown, Action<string>> {
  return (dispatch, getState): void => {
    const state = getState();
    const { hasIdentified, userContext = {} } = state && state.user;
    if (!userContext.is_guest && (!hasIdentified || forceIdentify)) {
      const userId = userContext.object_id;
      const traits = {
        email: userContext.email || '',
        first_name: userContext.first_name || '',
        last_name: userContext.last_name || '',
        role: userSelectors.getUserRole(state),
        name: userContext.first_name ? `${userContext.first_name} ${userContext.last_name}` : '',
      };
      trackIdentifyUser(userId, traits);

      if (window.analytics && window.dataLayer) {
        window.dataLayer.push({
          event: 'User Identified',
          userId,
        });
      }

      dispatch({ type: ActionType.IDENTIFY_USER });
    }
  };
}

export function identifyGuestUser(
  forceIdentify = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ThunkAction<void, any, unknown, Action<string>> {
  return (dispatch, getState): void => {
    const { hasGuestIdentified, userContext = {} } = getState().user;
    if (userContext.is_guest && (!hasGuestIdentified || forceIdentify)) {
      const userId = userContext.object_id;
      const traits = {
        role: 'VISITOR',
      };
      trackIdentifyUser(userId, traits);
      dispatch({
        type: ActionType.IDENTIFY_GUEST_USER,
      });
    }
  };
}

type SailthruRequest = {
  email: string;
  vars?: {
    email: string;
    first_account_created?: string;
    first_component?: string;
    first_account_device_type?: 'Mobile Web' | 'Desktop';
  };
  onSuccess: () => void;
  onError: () => void;
};

/**
 * Identify a user within Sailthru.
 * Result sets sailthru_hid with a SHA256 hash of the email
 * SEE: https://getstarted.sailthru.com/developers/api-client/javascript/#userSignUp
 * @param {string} email Users email
 * @returns {Promise<any>}
 */
export function identifyUserSailthru(
  email: string,
  newUserData:
    | (SaveResponseRequest & {
        business_unit_feature: BusinessUnitComponent;
        business_unit_source: WClientUserAccountView.BusinessUnitSourceEnum;
      })
    | undefined
): Promise<void> {
  return new Promise(resolve => {
    // Ignore if Sailthru is not available
    if (typeof window.Sailthru === 'undefined') {
      resolve();
      return;
    }

    // In case something goes terribly wrong
    const forceResolveTimeout = window.setTimeout(resolve, 1000);

    const vars: SailthruRequest['vars'] = { email };
    const newSailthruUserVarsFeature =
      window.zola && window.zola.feature && window.zola.feature.newSailthruUserVars;

    if (newUserData && newSailthruUserVarsFeature) {
      ((vars.first_account_created as unknown) as
        | WClientUserAccountView.BusinessUnitSourceEnum
        | undefined) = newUserData.business_unit_source;

      // TODO: Sailthru tracking should probably be moved entirely to the BE.
      vars.first_component = newUserData.business_unit_feature;
      vars.first_account_device_type = detectMobileDevice() ? 'Mobile Web' : 'Desktop';
    }

    const sailthruRequest: SailthruRequest = {
      email,
      vars,
      onSuccess: () => {
        clearTimeout(forceResolveTimeout);
        resolve();
      },
      onError: () => {
        clearTimeout(forceResolveTimeout);
        resolve();
      },
    };
    try {
      window.Sailthru.integration('userSignUp', sailthruRequest);
    } catch (error) {
      resolve();
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getUserContext(): ThunkAction<Promise<UserContext>, any, unknown, Action<string>> {
  return (dispatch, getState): Promise<UserContext> => {
    dispatch(requestUser());
    return ApiService.get('/website-nav/web-api/v1/user/get')
      .then((json: UserContext) => {
        dispatch(receiveUser(json));
        return json;
      })
      .then((json: UserContext) => {
        dispatch(identifyUser());
        return json;
      })
      .then((json: UserContext) => {
        // Temporarily set email for BounceX usage
        window.zola = window.zola || {};
        window.zola.tracking = {
          email: getState().user.userContext.email || '',
        };
        return json;
      })
      .then((json: UserContext) => {
        const { is_guest: isGuest } = getState().user.userContext;
        if (isGuest) {
          deleteCookie('zolaLoggedIn');
        } else {
          setCookie('zolaLoggedIn', true);
        }
        return json;
      });
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateUserStatus(): ThunkAction<void, any, unknown, Action<string>> {
  return (dispatch): UserActionTypes => {
    dispatch(requestUserStatus());
    return ApiService.get('/web-api/v1/user/status').then((json: { status: AuthStatusType }) =>
      dispatch(receiveUserStatus(json))
    );
  };
}



// WEBPACK FOOTER //
// ./src/actions/UserActions.ts